import { useEffect, useRef, useState } from 'react';
import {
  X,
  MapPin,
  Briefcase,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';
import type { Role } from './rolesData';
import { formatCompensation, formatSalaryRange } from './rolesData';

interface RoleDialogProps {
  role: Role | null; // null => "General Interest" application
  open: boolean;
  onClose: () => void;
}

interface FileState {
  file: File | null;
  error: string | null;
}

/** Reference to a file already streamed into Storage, handed to the submit call. */
interface UploadedRef {
  path: string;
  sig: string;
  fileName: string;
}

// Bounded by the `candidate-uploads` bucket's file_size_limit (10 MB), not by
// the API. Files are streamed straight to Supabase Storage through a signed
// URL, so Vercel's 4.5 MB function-body cap never sees them — see
// apps/website/server/careersUpload.ts for why that indirection exists.
const MAX_UPLOAD_MB = 10;
const ACCEPTED_MIME =
  '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Content-Type to send on the signed upload, derived from the file extension.
 *
 * The `candidate-uploads` bucket declares `allowed_mime_types` (PDF/DOC/DOCX
 * only) and Storage enforces it against the Content-Type header of the upload.
 * `File.type` is NOT reliable here — browsers report an empty string for .doc /
 * .docx often enough to matter (no registered handler on the OS), and falling
 * back to `application/octet-stream` would get the upload rejected outright,
 * blocking a candidate on exactly the formats we advertise.
 *
 * The extension is already validated before we get here, and the server sniffs
 * the stored bytes regardless, so nothing is trusted on the strength of this
 * header — it exists to satisfy the bucket's own allow-list.
 */
function uploadContentType(fileName: string): string {
  const ext = fileName.toLowerCase().split('.').pop();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'doc') return 'application/msword';
  return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
}

/**
 * Parse a response that *should* be JSON but might not be.
 *
 * Platform-level rejections (413, 502, gateway errors) come back as HTML or
 * plain text. The previous version called `response.json()` unconditionally, so
 * those surfaced as a bare "Network error" — the candidate had no idea their
 * application hadn't landed, and neither did we. Always give a real reason.
 */
async function readJsonResponse(res: Response): Promise<{ success: boolean; error?: string }> {
  const text = await res.text();
  try {
    return JSON.parse(text) as { success: boolean; error?: string };
  } catch {
    if (res.status === 413) {
      return {
        success: false,
        error: `That file is too large. Please keep it under ${MAX_UPLOAD_MB}MB.`,
      };
    }
    return {
      success: false,
      error: `Upload failed (error ${res.status}). Please try again, or email careers@criticalbridge.ai.`,
    };
  }
}

/**
 * Stream one file into Storage: ask the server for a signed URL scoped to a
 * path it chose, PUT the bytes straight to Supabase, and hand back the signed
 * path for the submit call to attach to the application row.
 */
async function uploadFile(file: File, kind: 'cv' | 'cover_letter'): Promise<UploadedRef> {
  const urlRes = await fetch('/api/careers-upload-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind, fileName: file.name, size: file.size }),
  });
  const urlJson = (await readJsonResponse(urlRes)) as {
    success: boolean;
    error?: string;
    data?: { signedUrl: string; path: string; sig: string };
  };
  if (!urlJson.success || !urlJson.data) {
    throw new Error(urlJson.error || 'Could not prepare the upload.');
  }

  const { signedUrl, path, sig } = urlJson.data;
  const putRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': uploadContentType(file.name) },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Could not upload ${file.name}. Please try again.`);
  }

  return { path, sig, fileName: file.name };
}

export function RoleDialog({ role, open, onClose }: RoleDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    linkedinUrl: '',
    portfolioUrl: '',
    coverLetterText: '',
    workAuthorization: '',
    heardAbout: '',
    website: '', // honeypot
  });

  const [cv, setCv] = useState<FileState>({ file: null, error: null });
  const [coverLetterFile, setCoverLetterFile] = useState<FileState>({
    file: null,
    error: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Reset on open/close and role change
  useEffect(() => {
    if (open) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        location: '',
        linkedinUrl: '',
        portfolioUrl: '',
        coverLetterText: '',
        workAuthorization: '',
        heardAbout: '',
        website: '',
      });
      setCv({ file: null, error: null });
      setCoverLetterFile({ file: null, error: null });
      setSubmitStatus('idle');
      setErrorMessage('');
      setProgress('');
    }
  }, [open, role?.id]);

  // While the dialog is open:
  //  - lock body scroll,
  //  - move focus into the dialog (WCAG expectation for modals),
  //  - trap Tab / Shift+Tab so focus can't escape to the underlying page,
  //  - Esc closes,
  //  - on close, return focus to the element that opened the dialog.
  useEffect(() => {
    if (!open) return;
    const dialogEl = dialogRef.current;
    const previousActive = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusableSelector =
      'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = (): HTMLElement[] =>
      dialogEl ? Array.from(dialogEl.querySelectorAll<HTMLElement>(focusableSelector)) : [];

    // Give React a tick to mount children before we grab focus.
    const focusTimer = window.setTimeout(() => {
      const focusable = getFocusable();
      (focusable[0] ?? dialogEl)?.focus();
    }, 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        dialogEl?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      // Return focus to the trigger (usually the role card the user activated)
      // so screen-reader users don't lose their place.
      if (previousActive && typeof previousActive.focus === 'function') {
        previousActive.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<FileState>>
  ) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setter({ file: null, error: null });
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setter({ file: null, error: `File too large (max ${MAX_UPLOAD_MB}MB).` });
      return;
    }
    if (!/\.(pdf|doc|docx)$/i.test(file.name)) {
      setter({ file: null, error: 'Please attach a PDF, DOC, or DOCX file.' });
      return;
    }
    // Bytes go to Storage at submit time, not now — holding a File reference
    // keeps a large CV out of memory while the candidate fills in the rest.
    setter({ file, error: null });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cv.file) {
      setCv({ ...cv, error: 'A CV is required.' });
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      setProgress('Uploading CV…');
      const cvRef = await uploadFile(cv.file, 'cv');

      let coverLetterRef: UploadedRef | null = null;
      if (coverLetterFile.file) {
        setProgress('Uploading cover letter…');
        coverLetterRef = await uploadFile(coverLetterFile.file, 'cover_letter');
      }

      setProgress('Submitting application…');
      const payload = {
        roleId: role?.id ?? 'general',
        roleTitle: role?.title ?? 'General Interest',
        ...formData,
        cv: cvRef,
        coverLetterFile: coverLetterRef,
      };

      const response = await fetch('/api/submit-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await readJsonResponse(response);

      if (result.success) {
        setSubmitStatus('success');
        trackEvent('careers', 'application_submit', role?.title ?? 'General Interest');
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.error || 'Something went wrong');
      }
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
      setProgress('');
    }
  };

  const roleTitle = role?.title ?? 'General Interest';
  const compensation = role ? formatCompensation(role.compensation) : null;
  const salaryRange = role ? formatSalaryRange(role.compensation) : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-dialog-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-none border bg-card shadow-2xl outline-none sm:h-auto sm:max-h-[92vh] sm:rounded-2xl"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b bg-card/95 p-6 backdrop-blur sm:p-8">
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-xs font-medium uppercase tracking-widest text-muted-foreground">
              {role ? role.department : 'Open Application'}
            </p>
            <h2 id="role-dialog-title" className="text-2xl font-bold tracking-tight sm:text-3xl">
              {roleTitle}
            </h2>
            {role && (
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" />
                  {role.location}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Briefcase className="h-4 w-4" />
                  {role.employmentType}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  Updated {role.updatedAt}
                </span>
                {compensation && (
                  <span className="rounded-full bg-primary/10 px-3 py-0.5 text-xs font-medium text-primary">
                    {compensation}
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-5">
          {/* JD */}
          <div className="overflow-y-auto border-b p-6 sm:p-8 lg:col-span-2 lg:border-b-0 lg:border-r">
            {role ? (
              <div className="space-y-8 text-sm">
                {role.about && role.about.length > 0 && (
                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      About CriticalBridge
                    </h3>
                    <div className="space-y-3">
                      {role.about.map((para, i) => (
                        <p key={i} className="leading-relaxed text-muted-foreground">
                          {para}
                        </p>
                      ))}
                    </div>
                  </section>
                )}

                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    About the role
                  </h3>
                  {role.tagline && (
                    <p className="mb-3 text-base font-medium text-primary">{role.tagline}</p>
                  )}
                  <p className="text-base leading-relaxed">{role.summary}</p>
                </section>

                <section>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    What you'll do
                  </h3>
                  <ul className="space-y-2">
                    {role.responsibilities.map((r) => (
                      <li key={r} className="flex gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span className="leading-relaxed">{r}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    What we're looking for
                  </h3>
                  <ul className="space-y-2">
                    {role.requirements.map((r) => (
                      <li key={r} className="flex gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span className="leading-relaxed">{r}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                {role.niceToHave && role.niceToHave.length > 0 && (
                  <section>
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Nice to have
                    </h3>
                    <ul className="space-y-2">
                      {role.niceToHave.map((r) => (
                        <li key={r} className="flex gap-2">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
                          <span className="leading-relaxed text-muted-foreground">{r}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {(role.stackSummary || (role.stack && role.stack.length > 0)) && (
                  <section>
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      {role.stackHeading ?? 'Our stack'}
                    </h3>
                    {role.stackSummary && (
                      <p className="leading-relaxed text-muted-foreground">{role.stackSummary}</p>
                    )}
                    {role.stack && role.stack.length > 0 && (
                      <ul className={`space-y-2 ${role.stackSummary ? 'mt-3' : ''}`}>
                        {role.stack.map((group) => (
                          <li key={group.label} className="leading-relaxed">
                            <span className="font-medium text-foreground">{group.label}:</span>{' '}
                            <span className="text-muted-foreground">{group.items}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                )}

                {role.howWeWork && (
                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      How we work
                    </h3>
                    <p className="leading-relaxed text-muted-foreground">{role.howWeWork}</p>
                  </section>
                )}

                {role.compensation &&
                  (salaryRange ||
                    role.compensation.equityNote ||
                    role.compensation.salaryNote ||
                    role.compensation.benefits?.length ||
                    role.compensation.notes) && (
                    <section>
                      <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Compensation
                      </h3>
                      {salaryRange && (
                        <p className="text-base font-semibold text-foreground">
                          {salaryRange}
                          <span className="font-normal text-muted-foreground"> / year</span>
                        </p>
                      )}
                      {role.compensation.equityNote && (
                        <p className="mt-1 text-sm text-foreground">
                          <span className="font-medium">Equity:</span>{' '}
                          <span className="text-muted-foreground">
                            {role.compensation.equityNote}
                          </span>
                        </p>
                      )}
                      {role.compensation.salaryNote && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {role.compensation.salaryNote}
                        </p>
                      )}
                      {role.compensation.benefits && role.compensation.benefits.length > 0 && (
                        <>
                          <p className="mt-4 text-sm font-medium text-foreground">
                            Additional compensation &amp; benefits
                          </p>
                          <ul className="mt-2 space-y-2">
                            {role.compensation.benefits.map((b) => (
                              <li key={b} className="flex gap-2">
                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                                <span className="leading-relaxed text-muted-foreground">{b}</span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {role.compensation.notes && !salaryRange && (
                        <p className="leading-relaxed text-muted-foreground">
                          {role.compensation.notes}
                        </p>
                      )}
                    </section>
                  )}

                <p className="border-t pt-6 text-xs text-muted-foreground">
                  CriticalBridge is an equal-opportunity employer.
                </p>
              </div>
            ) : (
              <div className="space-y-6 text-sm">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Don't see your role?
                </h3>
                <p className="text-base leading-relaxed">
                  We hire against needs, not org charts. If you would build category-defining work
                  alongside a small, senior team — send your story and we will read it.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Tell us what you want to work on, what you have already shipped, and who has
                  benefited from it.
                </p>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="overflow-y-auto p-6 sm:p-8 lg:col-span-3">
            {submitStatus === 'success' && (
              <div className="flex h-full flex-col items-center justify-center py-16 text-center">
                <CheckCircle className="mb-4 h-16 w-16 text-success" />
                <h3 className="mb-2 text-2xl font-bold">Application received</h3>
                <p className="mb-8 max-w-sm text-muted-foreground">
                  Thanks for applying to <span className="font-medium">{roleTitle}</span>. Our team
                  will review and reach out if there is a fit.
                </p>
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="flex h-full flex-col items-center justify-center py-16 text-center">
                <AlertCircle className="mb-4 h-16 w-16 text-destructive" />
                <h3 className="mb-2 text-2xl font-bold">Something went wrong</h3>
                <p className="mb-8 max-w-sm text-muted-foreground">
                  {errorMessage || 'Please try again or email us at careers@criticalbridge.ai.'}
                </p>
                <Button onClick={() => setSubmitStatus('idle')}>Try Again</Button>
              </div>
            )}

            {submitStatus === 'idle' && (
              <>
                <h3 className="mb-1 text-xl font-bold">Apply for this role</h3>
                <p className="mb-6 text-sm text-muted-foreground">
                  We read every application. Fields marked with an asterisk are required.
                </p>

                <form className="space-y-5" onSubmit={handleSubmit}>
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      id="firstName"
                      label="First name *"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      placeholder="Jane"
                    />
                    <Field
                      id="lastName"
                      label="Last name *"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      placeholder="Doe"
                    />
                  </div>

                  <Field
                    id="email"
                    label="Email *"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    placeholder="jane@example.com"
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      id="phone"
                      label="Phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder="+1 555 123 4567"
                    />
                    <Field
                      id="location"
                      label="Location"
                      value={formData.location}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder="City, Country"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      id="linkedinUrl"
                      label="LinkedIn"
                      type="url"
                      value={formData.linkedinUrl}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder="https://linkedin.com/in/…"
                    />
                    <Field
                      id="portfolioUrl"
                      label="Portfolio / GitHub"
                      type="url"
                      value={formData.portfolioUrl}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder="https://…"
                    />
                  </div>

                  <FileField
                    id="cv"
                    label={`CV / Resume * (PDF, DOC, DOCX — max ${MAX_UPLOAD_MB}MB)`}
                    file={cv.file}
                    error={cv.error}
                    disabled={isSubmitting}
                    onChange={(e) => handleFileChange(e, setCv)}
                    accept={ACCEPTED_MIME}
                  />

                  <FileField
                    id="coverLetterFile"
                    label={`Cover letter (optional file — max ${MAX_UPLOAD_MB}MB)`}
                    file={coverLetterFile.file}
                    error={coverLetterFile.error}
                    disabled={isSubmitting}
                    onChange={(e) => handleFileChange(e, setCoverLetterFile)}
                    accept={ACCEPTED_MIME}
                  />

                  <div>
                    <label htmlFor="coverLetterText" className="mb-2 block text-sm font-medium">
                      Or write a short note
                    </label>
                    <textarea
                      id="coverLetterText"
                      name="coverLetterText"
                      value={formData.coverLetterText}
                      onChange={handleChange}
                      rows={4}
                      disabled={isSubmitting}
                      className="w-full resize-none rounded-lg border bg-background px-4 py-3 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Why this role, at CriticalBridge, right now?"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="workAuthorization" className="mb-2 block text-sm font-medium">
                        Work authorization
                      </label>
                      <select
                        id="workAuthorization"
                        name="workAuthorization"
                        value={formData.workAuthorization}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="w-full rounded-lg border bg-background px-4 py-3 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="">Select…</option>
                        <option value="authorized">Authorized to work in role location</option>
                        <option value="needs_sponsorship">Will need sponsorship</option>
                        <option value="prefer_not_to_say">Prefer not to say</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="heardAbout" className="mb-2 block text-sm font-medium">
                        How did you hear about us?
                      </label>
                      <select
                        id="heardAbout"
                        name="heardAbout"
                        value={formData.heardAbout}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="w-full rounded-lg border bg-background px-4 py-3 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="">Select…</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="referral">Referral from someone at CriticalBridge</option>
                        <option value="search">Search / news</option>
                        <option value="event">Event or conference</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {progress || 'Sending…'}
                      </>
                    ) : (
                      'Submit application'
                    )}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    By submitting this form, you agree to our{' '}
                    <a href="/privacy" className="underline hover:text-primary">
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required,
  disabled,
  placeholder,
}: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium">
        {label}
      </label>
      <input
        type={type}
        id={id}
        name={id}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full rounded-lg border bg-background px-4 py-3 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
      />
    </div>
  );
}

interface FileFieldProps {
  id: string;
  label: string;
  file: File | null;
  error: string | null;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  accept: string;
}

function FileField({ id, label, file, error, disabled, onChange, accept }: FileFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-medium">
        {label}
      </label>
      <label
        htmlFor={id}
        className={`flex cursor-pointer items-center gap-3 rounded-lg border border-dashed bg-background px-4 py-4 text-sm transition-colors hover:border-primary hover:bg-primary/5 ${
          disabled ? 'cursor-not-allowed opacity-60' : ''
        }`}
      >
        <Upload className="h-5 w-5 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate">
          {file ? (
            <>
              <span className="font-medium text-foreground">{file.name}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">Click to choose a file</span>
          )}
        </span>
        <input
          type="file"
          id={id}
          name={id}
          className="sr-only"
          accept={accept}
          onChange={onChange}
          disabled={disabled}
        />
      </label>
      {error && (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
